import React, { Component } from 'react';
import { connect } from 'react-redux';
import TextField from '@material-ui/core/TextField';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Collapse from '@material-ui/core/Collapse';
import { getFullSchema } from '../../data-dictionary.actions';
import SchemaPicker from '../../../database/components/schema-picker';
import { buildItemId, schemaOf } from '../item-id';

// Keys returned by getFullSchema that are internal metadata and should NOT
// appear as selectable items in the Schema Selection wizard step.
const NON_SELECTABLE_KEYS = [
  'constraints',
  'foreignKeys',
  'indexes',
  'tableSizes',
  'triggers',
  'viewColumnDependencies',
  'viewDependencies',
];
const ACCORDION_EXCLUDED_KEYS = ['schemas'];
// Only these three types are shown as selectable top-level objects in the wizard.
// All other types are fetched and saved server-side at import time.
const PRIMARY_SELECTABLE_KEYS = ['tables', 'views', 'storedProcedures'];

export class Step2SchemaSelection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      searchQuery: '',
      expandedGroups: {} // Dynamic: populated based on schema metadata
    };
  }

  // Initialize expanded groups dynamically when metadata is loaded
  initializeExpandedGroups = (schemaMetadata) => {
    if (!schemaMetadata) return;
    
    const expandedGroups = {};
    Object.keys(schemaMetadata).forEach(key => {
      if (
        !ACCORDION_EXCLUDED_KEYS.includes(key) &&
        Array.isArray(schemaMetadata[key]) &&
        schemaMetadata[key].length > 0
      ) {
        expandedGroups[key] = false; // All collapsed by default
      }
    });
    
    this.setState({ expandedGroups });
  }

  componentDidMount() {
    const { selectedConnection, schemaMetadata } = this.props;
    const hasConnection = selectedConnection && selectedConnection.connId;
    if (!schemaMetadata && hasConnection && this.selectedSchemaNames().length > 0) {
      this.loadSchemaMetadata();
    }
  }

  loadSchemaMetadata = async () => {
    try {
      // Get connection ID from Step 1
      const { selectedConnection, selectedDatabase } = this.props;

      if (!selectedConnection || !selectedConnection.connId) {
        this.setState({
          error: 'No database connection selected'
        });
        return;
      }

      const schemas = this.selectedSchemaNames();

      if (schemas.length === 0) {
        return;
      }

      // One request per selected schema, merged into a single dispatch by the
      // action — the reducer holds one fullSchemaData, so N dispatches would
      // clobber each other.
      this.props.getFullSchema(selectedConnection.connId, schemas, selectedDatabase || schemas[0]);
    } catch (error) {
      console.error('Error loading schema metadata:', error);
      this.setState({ 
        error: error.message || 'Failed to load schema metadata'
      });
    }
  }

  componentDidUpdate(prevProps) {
    const prevConnectionId = prevProps.selectedConnection?.connId;
    const currentConnectionId = this.props.selectedConnection?.connId;
    const connectionChanged = prevConnectionId !== currentConnectionId;
    // Compare the whole selection, not just the first schema — adding a second
    // schema to an MSSQL import leaves selectedSchema unchanged.
    const schemaKey = (p) => this.selectedSchemaNames(p).join('|');
    const selectedSchemaChanged = schemaKey(prevProps) !== schemaKey(this.props);

    if (connectionChanged || selectedSchemaChanged) {
      this.setState({ error: null });
    }

    if (
      currentConnectionId &&
      schemaKey(this.props) &&
      (connectionChanged || selectedSchemaChanged)
    ) {
      this.loadSchemaMetadata();
    }

    // Handle successful schema load
    if (prevProps.fullSchemaData !== this.props.fullSchemaData && this.props.fullSchemaData) {
      const transformedData = this.transformSchemaData(this.props.fullSchemaData);
      this.props.onUpdate({ 
        schemaMetadata: transformedData,
        fullSchemaData: this.props.fullSchemaData // Store raw data for column access
      });
      this.initializeExpandedGroups(transformedData);
    }
  }

  transformSchemaData = (schemaData) => {
    const transformed = {};
    const schemaMap = new Map();
    
    // Common field name mappings for different object types
    const nameFieldMap = {
      tables: 'tableName',
      views: 'viewName',
      storedProcedures: 'procedureName',
      functions: 'functionName',
      triggers: 'triggerName',
      sequences: 'sequenceName',
      synonyms: 'synonymName',
      packages: 'packageName',
      userDefinedTypes: 'typeName',
      foreignKeys: 'fkName',
      indexes: 'indexName',
      constraints: 'constraintName'
    };

    // Process each category dynamically
    Object.keys(schemaData).forEach(categoryKey => {
      // Skip internal metadata keys — these are not user-selectable schema objects
      if (NON_SELECTABLE_KEYS.includes(categoryKey)) return;

      const items = schemaData[categoryKey];
      
      if (!Array.isArray(items) || items.length === 0) return;

      // Determine the name field for this category
      const nameField = nameFieldMap[categoryKey] || 'name';
      
      transformed[categoryKey] = items.map((item, index) => {
        const schemaName = schemaOf(item);
        schemaMap.set(schemaName, (schemaMap.get(schemaName) || 0) + 1);
        
        // id comes from the shared helper so step 5 resolves the same identity.
        const transformedItem = {
          id: buildItemId(item, categoryKey, index),
          name: item[nameField] || item.name || `${categoryKey}_${index}`,
          schema: schemaName,
          type: categoryKey.slice(0, -1) // Remove trailing 's' for type
        };

        // Add columnCount if columns exist
        if (item.columns && Array.isArray(item.columns)) {
          transformedItem.columnCount = item.columns.length;
        }

        // Add paramCount if parameters exist
        if (item.parameters && Array.isArray(item.parameters)) {
          transformedItem.paramCount = item.parameters.length;
        }

        // Copy other useful metadata fields
        if (item.count !== undefined) transformedItem.count = item.count;
        if (item.eventCount !== undefined) transformedItem.eventCount = item.eventCount;
        if (item.currentValue !== undefined) transformedItem.currentValue = item.currentValue;
        if (item.baseObject !== undefined) transformedItem.baseObject = item.baseObject;

        return transformedItem;
      });
    });

    // Build schemas array
    transformed.schemas = Array.from(schemaMap.entries()).map(([name, count], index) => ({
      id: `schema_${index}`,
      name: name,
      count: count
    }));

    return transformed;
  }

  // Transformed metadata for the CURRENT fullSchemaData.
  //
  // schemaMetadata is persisted into wizard state for the later steps, but that is a
  // round trip behind redux (SUCCESS → componentDidUpdate → onUpdate → setState).
  // Rendering from the prop meant one frame showing the previous selection's counts
  // after the load had already finished — 142 tables where 185 was correct. Deriving
  // here keeps what is on screen tied to the data that is actually loaded.
  //
  // Memoized on the fullSchemaData reference: the transform walks every object, and
  // render runs on every keystroke in the search box.
  currentSchemaMetadata = () => {
    const { fullSchemaData, schemaMetadata } = this.props;
    // Before the fetch resolves there is no fullSchemaData; fall back to whatever
    // metadata the wizard already holds (edit mode populates both).
    if (!fullSchemaData) return schemaMetadata;
    if (this._metadataSource !== fullSchemaData) {
      this._metadataSource = fullSchemaData;
      this._metadata = this.transformSchemaData(fullSchemaData);
    }
    return this._metadata;
  }

  // The one place the selected-schema list is derived. One schema per import now,
  // but the list shape is kept because everything downstream — the load checklist,
  // the object grouping, the import payload — already speaks it, and dictionaries
  // imported before this change still hold several schemas.
  selectedSchemaNames = (props = this.props) => {
    const { selectedSchema } = props;
    return selectedSchema ? [selectedSchema] : [];
  }

  // Per-schema load checklist. Each selected schema gets a row that resolves on its
  // own, so a finished load is visibly different from a stalled one — and a schema
  // that failed is named rather than collapsing the whole step into one error.
  renderSchemaProgress = () => {
    const rows = this.props.schemaLoadProgress || [];
    if (rows.length === 0) return null;

    const done = rows.filter((r) => r.status === 'done').length;
    const failed = rows.filter((r) => r.status === 'error');
    const finished = rows.every((r) => r.status !== 'loading');

    const summary = (counts) => [
      `${counts.tables} ${counts.tables === 1 ? 'table' : 'tables'}`,
      `${counts.views} ${counts.views === 1 ? 'view' : 'views'}`,
      `${counts.storedProcedures} ${counts.storedProcedures === 1 ? 'procedure' : 'procedures'}`,
    ].join(' · ');

    return (
      <div className="schema-progress" data-testid="dd-schema-progress">
        <div className="schema-progress-head">
          <span className="schema-progress-title">
            {finished
              ? (failed.length > 0 ? 'Some schemas could not be read' : 'All schemas read')
              : 'Reading schemas…'}
          </span>
          <span className="schema-progress-count">{done} of {rows.length} complete</span>
        </div>
        <ul className="schema-progress-list">
          {rows.map((row) => (
            <li
              key={row.schema}
              className={`schema-progress-row is-${row.status}`}
              data-testid={`dd-schema-progress-${row.status}`}
            >
              {/* CSS spinner rather than CircularProgress: the app's global MUI
                  overrides restyle the SVG, so the ring rendered thin and
                  misaligned against the row text. */}
              <span className="schema-progress-state" aria-hidden="true">
                {row.status === 'loading' && <span className="schema-progress-spin" />}
                {row.status === 'done' && <span className="material-icons">check_circle</span>}
                {row.status === 'error' && <span className="material-icons">error</span>}
              </span>
              <span className="schema-progress-name">{row.schema}</span>
              <span className="schema-progress-detail">
                {row.status === 'loading' && 'reading…'}
                {row.status === 'done' && row.counts && summary(row.counts)}
                {row.status === 'error' && row.error}
              </span>
            </li>
          ))}
        </ul>
        {finished && failed.length > 0 && (
          <div className="schema-progress-footer">
            <span>
              {failed.length === 1
                ? `“${failed[0].schema}” could not be read, so this dictionary would be incomplete.`
                : `${failed.length} schemas could not be read, so this dictionary would be incomplete.`}
              {' '}Retry, or clear them from the selection to continue.
            </span>
            <button className="btn btn-default" onClick={this.loadSchemaMetadata} data-testid="dd-schema-progress-retry">
              Retry
            </button>
          </div>
        )}
      </div>
    );
  }

  // SchemaPicker takes a bare string for single-level engines and
  // {databaseName, schemaName} for MS SQL Server.
  pickerValue = () => {
    const { selectedConnection, selectedSchema, selectedDatabase } = this.props;
    const type = String((selectedConnection && (selectedConnection.type || selectedConnection.dbType)) || '').toLowerCase();
    if (type !== 'mssql') return selectedSchema;
    return {
      databaseName: selectedDatabase || '',
      schemaName: selectedSchema || '',
    };
  }

  handleSearchChange = (event) => {
    this.setState({ searchQuery: event.target.value });
  }

  handleGroupToggle = (group) => {
    this.setState(prevState => ({
      expandedGroups: {
        ...prevState.expandedGroups,
        [group]: !prevState.expandedGroups[group]
      }
    }));
  }

  handleSelectAll = (type, items) => {
    // Nothing to toggle when the active search hides every item in this category
    // ([].every() is vacuously true, which would otherwise take the wrong branch).
    // The Select All control is also disabled in this state (#603).
    if (!items || items.length === 0) return;
    const itemIds = items.map(item => item.id);
    const currentSelection = this.props.selectedItems[type] || [];
    const allSelected = itemIds.every(id => currentSelection.includes(id));
    
    const newSelection = allSelected 
      ? currentSelection.filter(id => !itemIds.includes(id))
      : [...new Set([...currentSelection, ...itemIds])];
    
    this.props.onUpdate({
      selectedItems: {
        ...this.props.selectedItems,
        [type]: newSelection
      }
    });
  }

  handleItemToggle = (type, itemId) => {
    const currentSelection = this.props.selectedItems[type] || [];
    const newSelection = currentSelection.includes(itemId)
      ? currentSelection.filter(id => id !== itemId)
      : [...currentSelection, itemId];
    
    this.props.onUpdate({
      selectedItems: {
        ...this.props.selectedItems,
        [type]: newSelection
      }
    });
  }

  // Keeps items belonging to ANY selected schema. This used to narrow to a single
  // schema, which was right when only one could be active; with MS SQL multi-select
  // every returned schema is deliberately chosen, so narrowing to the first one hid
  // the rest and under-reported the object counts.
  getVisibleItemsForSchema = (items, selectedSchemaNames, hasMultipleSchemas) => {
    if (!Array.isArray(items)) return [];
    if (!hasMultipleSchemas) return items;
    const wanted = Array.isArray(selectedSchemaNames)
      ? selectedSchemaNames
      : (selectedSchemaNames ? [selectedSchemaNames] : []);
    if (wanted.length === 0) return [];
    const wantedSet = new Set(wanted);
    return items.filter(item => wantedSet.has(item.schema));
  }

  createEmptySelection = () => {
    const keys = new Set([
      'schemas',
      'tables',
      'views',
      'storedProcedures',
      ...Object.keys(this.props.selectedItems || {})
    ]);

    const nextSelection = {};
    keys.forEach((key) => {
      nextSelection[key] = [];
    });
    return nextSelection;
  }

  countSelectedItemsExcludingSchemas = () => {
    return Object.entries(this.props.selectedItems || {}).reduce((sum, [key, items]) => {
      if (key === 'schemas') return sum;
      return sum + (Array.isArray(items) ? items.length : 0);
    }, 0);
  }

  // SchemaPicker emits a bare string for single-level engines and
  // {databaseName, schemaName} for MS SQL Server, where the database and the
  // schema are two different names. One schema per import on both.
  handleSchemaChange = (selection) => {
    const isTwoLevel = selection && typeof selection === 'object';
    const nextSchemas = isTwoLevel
      ? (selection.schemaName ? [selection.schemaName] : [])
      : (selection ? [selection] : []);
    const nextDatabase = isTwoLevel ? selection.databaseName : selection;

    const currentSchemas = this.selectedSchemaNames();
    const selectedCount = this.countSelectedItemsExcludingSchemas();
    const changed = currentSchemas.join(' ') !== nextSchemas.join(' ');

    if (currentSchemas.length > 0 && changed && selectedCount > 0) {
      const confirmed = window.confirm('Changing the schema selection will reset your current object selections. Continue?');
      if (!confirmed) return;
    }

    this.props.onUpdate({
      selectedDatabase: nextDatabase,
      selectedSchema: nextSchemas[0] || null,
      schemaMetadata: null,
      fullSchemaData: null,
      selectedItems: this.createEmptySelection()
    });
  }

  filterItems = (items) => {
    if (!items || !Array.isArray(items)) return [];
    const query = this.state.searchQuery ? this.state.searchQuery.toLowerCase() : '';
    if (!query) return items;
    return items.filter(item => item && item.name && typeof item.name === 'string' && item.name.toLowerCase().includes(query));
  }

  // Generate category display configuration dynamically from data
  getCategoryConfig = (categoryKey, items) => {
    // Default configuration fallback
    const defaultConfigs = {
      tables: { label: 'Tables', metaField: 'columnCount', metaLabel: 'columns' },
      views: { label: 'Views', metaField: 'columnCount', metaLabel: 'columns' },
      storedProcedures: { label: 'Stored Procedures', metaField: 'paramCount', metaLabel: 'parameters' },
      functions: { label: 'Functions', metaField: 'paramCount', metaLabel: 'parameters' },
      triggers: { label: 'Triggers', metaField: 'eventCount', metaLabel: 'events' },
      sequences: { label: 'Sequences', metaField: 'currentValue', metaLabel: 'current value' },
      synonyms: { label: 'Synonyms', metaField: 'baseObject', metaLabel: 'base object' },
      packages: { label: 'Packages', metaField: 'procedureCount', metaLabel: 'procedures' },
      userDefinedTypes: { label: 'User Defined Types', metaField: 'columns', metaLabel: 'columns' }
    };

    // If we have a default config, use it
    if (defaultConfigs[categoryKey]) {
      return defaultConfigs[categoryKey];
    }

    // Otherwise, generate dynamically from the category key
    const label = categoryKey
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();

    // Try to infer metadata field from the first item
    let metaField = null;
    let metaLabel = 'items';
    
    if (items && items.length > 0) {
      const firstItem = items[0];
      // Look for common metadata fields
      if (firstItem.columnCount !== undefined) {
        metaField = 'columnCount';
        metaLabel = 'columns';
      } else if (firstItem.paramCount !== undefined) {
        metaField = 'paramCount';
        metaLabel = 'parameters';
      } else if (firstItem.count !== undefined) {
        metaField = 'count';
        metaLabel = 'items';
      } else if (firstItem.eventCount !== undefined) {
        metaField = 'eventCount';
        metaLabel = 'events';
      }
    }

    return { label, metaField, metaLabel };
  }

  // Render a single category accordion dynamically
  renderCategoryAccordion = (categoryKey, items, config) => {
    const { selectedItems } = this.props;
    const { expandedGroups } = this.state;
    const filteredItems = this.filterItems(items);
    
    if (!items || items.length === 0) return null;

    return (
      <div key={categoryKey} className="schema-group">
        <div className="schema-group-header" onClick={() => this.handleGroupToggle(categoryKey)}>
          <span className="material-icons">
            {expandedGroups[categoryKey] ? 'expand_more' : 'chevron_right'}
          </span>
          <h4>{config.label} ({filteredItems.length})</h4>
          <FormControlLabel
            control={
              <Checkbox
                checked={filteredItems.length > 0 && filteredItems.every(item => selectedItems[categoryKey]?.includes(item.id))}
                indeterminate={filteredItems.some(item => selectedItems[categoryKey]?.includes(item.id)) &&
                              !filteredItems.every(item => selectedItems[categoryKey]?.includes(item.id))}
                onChange={(e) => { e.stopPropagation(); this.handleSelectAll(categoryKey, filteredItems); }}
                onClick={(e) => e.stopPropagation()}
                inputProps={{'data-testid': 'dd-select-all'}}
              />
            }
            label="Select All"
            // Disable when the search hides every item in this category, so it
            // can't silently select the (hidden) full list (#603).
            disabled={filteredItems.length === 0}
            data-testid={`select-all-${categoryKey}`}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        {/* unmountOnExit: groups start collapsed, and without it every object in
            every group is mounted as a MUI Checkbox and re-rendered on each
            keystroke in the search box. Select All / indeterminate state is derived
            from filteredItems above, not from the mounted rows, so nothing changes
            but the render cost. */}
        <Collapse in={expandedGroups[categoryKey]} unmountOnExit>
          <div className="schema-items">
            {filteredItems.map(item => (
              <div key={item.id} className="schema-item">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedItems[categoryKey]?.includes(item.id) || false}
                      onChange={() => this.handleItemToggle(categoryKey, item.id)}
                      inputProps={{'data-testid': 'dd-object-checkbox', 'data-object-name': item.name}}
                    />
                  }
                  label={
                    <div className="item-details">
                      <span className="item-name">{item.name}</span>
                      {item[config.metaField] !== undefined && (
                        <span className="item-meta">{item[config.metaField]} {config.metaLabel}</span>
                      )}
                    </div>
                  }
                />
              </div>
            ))}
          </div>
        </Collapse>
      </div>
    );
  }

  render() {
    const { selectedConnection, selectedItems, selectedSchema, isLoading } = this.props;
    // Derived from the loaded data, not the round-tripped prop — see currentSchemaMetadata.
    const schemaMetadata = this.currentSchemaMetadata();
    const { searchQuery, error } = this.state;
    const hasConnection = selectedConnection && selectedConnection.connId;
    const isFullSchemaLoading = isLoading && isLoading.includes('fullschema');

    if (hasConnection && !selectedSchema) {
      return (
        <div className="step2-container">
          <div className="schema-group schema-picker-card">
            <div className="schema-group-header schema-picker-header">
              <span className="material-icons">folder_open</span>
              <h4>Select a Database Schema</h4>
            </div>
            <div className="schema-picker-body">
              <SchemaPicker
                connectionId={selectedConnection.connId}
                connectionType={selectedConnection?.type || selectedConnection?.dbType}
              value={this.pickerValue()}
                label="Choose a schema"
                onChange={this.handleSchemaChange}
                excludeDictionaryName={this.props.currentDictionaryName}
              />
            </div>
          </div>
        </div>
      );
    }

    if (!hasConnection) {
      return <div>No database connection selected</div>;
    }

    if (!schemaMetadata) {
      return (
        <div className="step2-container">
          <div className="schema-group schema-picker-card">
            <div className="schema-group-header schema-picker-header">
              <span className="material-icons">folder_open</span>
              <h4>Select a Database Schema</h4>
            </div>
            <div className="schema-picker-body">
              <SchemaPicker
                connectionId={selectedConnection?.connId}
                connectionType={selectedConnection?.type || selectedConnection?.dbType}
              value={this.pickerValue()}
                label="Choose a schema"
                onChange={this.handleSchemaChange}
                excludeDictionaryName={this.props.currentDictionaryName}
              />
            </div>
          </div>
          {(isFullSchemaLoading || (this.props.schemaLoadProgress || []).length > 0) && this.renderSchemaProgress()}
          {error && (
            <div className="step2-error">
              <span className="material-icons" style={{ fontSize: 48, color: '#f44336' }}>error_outline</span>
              <h3>Failed to Load Schema</h3>
              <p>{error}</p>
              <button
                className="btn btn-action"
                onClick={this.loadSchemaMetadata}
                style={{ marginTop: 16 }}
              >
                Retry
              </button>
            </div>
          )}
        </div>
      );
    }

    const schemaOptions = schemaMetadata.schemas || [];
    const hasMultipleSchemas = schemaOptions.length > 1;

    const categories = PRIMARY_SELECTABLE_KEYS.filter(key =>
      Array.isArray(schemaMetadata[key]) &&
      schemaMetadata[key].length > 0
    );

    const visibleItemsByCategory = categories.reduce((acc, key) => {
      acc[key] = this.getVisibleItemsForSchema(schemaMetadata[key], this.selectedSchemaNames(), hasMultipleSchemas);
      return acc;
    }, {});
    
    const totalCount = categories.reduce((sum, key) =>
      sum + visibleItemsByCategory[key].length, 0
    );

    const selectedCount = categories.reduce((sum, key) => {
      const selected = selectedItems[key] || [];
      const visibleIds = visibleItemsByCategory[key].map(item => item.id);
      return sum + visibleIds.filter(id => selected.includes(id)).length;
    }, 0);

    return (
      <div className="step2-container">
        {hasConnection && (
          <div className="schema-group schema-picker-card">
            <div className="schema-group-header schema-picker-header">
              <span className="material-icons">folder_open</span>
              <h4>Select a Database Schema</h4>
            </div>
            <div className="schema-picker-body">
              <SchemaPicker
                connectionId={selectedConnection.connId}
                connectionType={selectedConnection?.type || selectedConnection?.dbType}
              value={this.pickerValue()}
                label="Choose a schema"
                onChange={this.handleSchemaChange}
                excludeDictionaryName={this.props.currentDictionaryName}
              />
            </div>
          </div>
        )}

        {(isFullSchemaLoading || (this.props.schemaLoadProgress || []).length > 0) && this.renderSchemaProgress()}

        {error && (
          <div className="step2-error">
            <span className="material-icons" style={{ fontSize: 48, color: '#f44336' }}>error_outline</span>
            <h3>Failed to Load Schema</h3>
            <p>{error}</p>
            <button
              className="btn btn-action"
              onClick={this.loadSchemaMetadata}
              style={{ marginTop: 16 }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Results stay hidden while a load is in flight. schemaMetadata lags redux
            by a round trip (SUCCESS → transformSchemaData → onUpdate → wizard
            setState), so rendering it mid-load showed the PREVIOUS selection's
            objects under a heading that says "Complete" — 142 tables while the
            checklist above already read 2 of 2. The checklist is the feedback during
            the load; everything below appears together, once, and consistent. */}
        {!isFullSchemaLoading && (
          <>
            <div className="schema-summary">
              <h3>Schema Analysis Complete</h3>
              <div className="summary-stats">
                {categories.map(key => {
                  const config = this.getCategoryConfig(key, schemaMetadata[key]);
                  return (
                    <div key={key} className="stat-item">
                      <span className="stat-number">{visibleItemsByCategory[key]?.length || 0}</span>
                      <span className="stat-label">{config.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="selection-header">
              <TextField
                placeholder="Search items..."
                value={searchQuery}
                onChange={this.handleSearchChange}
                fullWidth
                InputProps={{
                  startAdornment: <span className="material-icons">search</span>
                }}
              />
              <div className="selection-count">
                {selectedCount} of {totalCount} items selected
              </div>
            </div>

            {categories.map(key =>
              this.renderCategoryAccordion(key, visibleItemsByCategory[key], this.getCategoryConfig(key, visibleItemsByCategory[key]))
            )}

            {categories.length === 0 && (
              <div className="step2-error">
                <p>No schema objects available.</p>
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  fullSchemaData: state.dataDictionaryReducer.fullSchemaData,
  isLoading: state.dataDictionaryReducer.isLoading,
  schemaLoadProgress: state.dataDictionaryReducer.schemaLoadProgress,
  failedSchemas: state.dataDictionaryReducer.failedSchemas,
  error: state.dataDictionaryReducer.error
});

export const mapDispatchToProps = (dispatch) => ({
  // Forward databaseName too. Dropping it makes the server fall back to treating
  // the schema as the database, and MS SQL Server reports that as
  // "Login failed for user ..." — an auth error for what is really a wrong-database
  // request.
  getFullSchema: (dbConnectionId, schemaName, databaseName) =>
    dispatch(getFullSchema(dbConnectionId, schemaName, databaseName))
});

export default connect(mapStateToProps, mapDispatchToProps)(Step2SchemaSelection);
